/**
 * useMonitoringControls — admin/founder per-room and per-user
 * monitoring mode management for RAELA Function 1.
 *
 * Member-facing monitoring CANNOT be disabled by members (V2 §13).
 * These controls are admin-only and gated by has_role('admin') RLS.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MonitoringMode = "off" | "standard" | "strict" | "shadow";

export interface RoomMonitoring {
  id: string;
  app: string;
  slug: string;
  name: string;
  monitoring_mode: MonitoringMode;
}

export const useRoomMonitoring = () => {
  const [rooms, setRooms] = useState<RoomMonitoring[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("community_rooms")
      .select("id, app, slug, name, monitoring_mode")
      .is("archived_at", null)
      .order("app")
      .order("name");
    if (err) setError(err.message);
    setRooms((data as RoomMonitoring[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setMode = useCallback(
    async (roomId: string, mode: MonitoringMode) => {
      const { error: err } = await supabase
        .from("community_rooms")
        .update({ monitoring_mode: mode })
        .eq("id", roomId);
      if (!err) {
        setRooms((prev) =>
          prev.map((r) => (r.id === roomId ? { ...r, monitoring_mode: mode } : r)),
        );
      }
      return { error: err?.message ?? null };
    },
    [],
  );

  return { rooms, loading, error, reload: load, setMode };
};

export interface UserMonitoring {
  id: string;
  display_name: string | null;
  username: string | null;
  monitoring_mode_override: MonitoringMode | null;
  shadow_monitoring_until: string | null;
  ai_training_opt_in: boolean;
}

export const useUserMonitoringSearch = () => {
  const [results, setResults] = useState<UserMonitoring[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from("profiles")
      .select(
        "id, display_name, username, monitoring_mode_override, shadow_monitoring_until, ai_training_opt_in",
      )
      .order("display_name", { ascending: true })
      .limit(50);
    if (q.trim()) {
      query = query.or(
        `display_name.ilike.%${q}%,username.ilike.%${q}%`,
      );
    } else {
      // Default view: members with active overrides
      query = supabase
        .from("profiles")
        .select(
          "id, display_name, username, monitoring_mode_override, shadow_monitoring_until, ai_training_opt_in",
        )
        .not("monitoring_mode_override", "is", null)
        .order("display_name", { ascending: true })
        .limit(50);
    }
    const { data, error: err } = await query;
    if (err) setError(err.message);
    setResults((data as UserMonitoring[] | null) ?? []);
    setLoading(false);
  }, []);

  const setOverride = useCallback(
    async (userId: string, mode: MonitoringMode | null) => {
      const { error: err } = await supabase
        .from("profiles")
        .update({ monitoring_mode_override: mode })
        .eq("id", userId);
      if (!err) {
        setResults((prev) =>
          prev.map((r) =>
            r.id === userId ? { ...r, monitoring_mode_override: mode } : r,
          ),
        );
      }
      return { error: err?.message ?? null };
    },
    [],
  );

  const setShadowWindow = useCallback(
    async (userId: string, hours: number | null) => {
      const until =
        hours == null
          ? null
          : new Date(Date.now() + hours * 3600 * 1000).toISOString();
      const { error: err } = await supabase
        .from("profiles")
        .update({ shadow_monitoring_until: until })
        .eq("id", userId);
      if (!err) {
        setResults((prev) =>
          prev.map((r) =>
            r.id === userId ? { ...r, shadow_monitoring_until: until } : r,
          ),
        );
      }
      return { error: err?.message ?? null };
    },
    [],
  );

  return { results, loading, error, search, setOverride, setShadowWindow };
};

export interface TrainingStats {
  optedInMembers: number;
  totalSamples: number;
  last24h: number;
}

export const useTrainingStats = () => {
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const [opted, total, recent] = await Promise.all([
      supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("ai_training_opt_in", true),
      supabase
        .from("raela_training_samples")
        .select("id", { count: "exact", head: true }),
      supabase
        .from("raela_training_samples")
        .select("id", { count: "exact", head: true })
        .gte("created_at", since),
    ]);
    setStats({
      optedInMembers: opted.count ?? 0,
      totalSamples: total.count ?? 0,
      last24h: recent.count ?? 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { stats, loading, reload: load };
};
